import { useState, useRef, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { MapPin, ChevronDown, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Business } from '@/types'

interface BranchPickerProps {
  businesses: Business[]
  currentBusiness: Business | null
  orgSlug: string
}

export function BranchPicker({ businesses, currentBusiness, orgSlug }: BranchPickerProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Hide when only one business
  if (businesses.length <= 1) return null

  const handleSwitch = (businessId: number) => {
    if (businessId === currentBusiness?.id) {
      setOpen(false)
      return
    }
    router.post(
      `/${orgSlug}/switch_business`,
      { business_id: businessId },
      {
        preserveState: false,
        onFinish: () => setOpen(false),
      }
    )
  }

  return (
    <div ref={ref} className="relative px-3 pb-2">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
      >
        <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate text-left">{currentBusiness?.name}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="absolute left-3 right-3 z-50 mt-1 rounded-lg border border-sidebar-border bg-sidebar shadow-lg">
          {businesses.map((biz) => (
            <button
              key={biz.id}
              onClick={() => handleSwitch(biz.id)}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent first:rounded-t-lg last:rounded-b-lg transition-colors"
            >
              <span className="flex-1 truncate text-left">{biz.name}</span>
              {biz.id === currentBusiness?.id && (
                <Check className="h-3.5 w-3.5 text-primary" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
